import { Inject, Injectable } from '@angular/core';
import * as coreRestPipeline from '@azure/core-rest-pipeline';
import { from, Observable, of } from 'rxjs';
import { Environment } from '@nx-ng/types';
import { ENVIRONMENT } from '@modules/core';
import {
  CheckSwiftCardResponse,
  ContentType,
  CreateCustomerAccountResponse,
  CreateCustomerRequest,
  CustomerAccountProfileImageResponse,
  CustomerDetailsAPI,
  CustomerDetailsAPIOptionalParams,
  GetApplicantSwiftcardsResponse,
  GetCustomerAccountResponse,
  GetCustomerApplicantsResponse,
  MobileAppVerifyResponse,
  UpdateCustomerAccountResponse,
  UpdateCustomerRequest,
  UploadCustomerAccountPhotoResponse,
} from '../client/src';
import { AddHeadersRequestPolicy } from '../util/AddHeadersRequestPolicy';
import { MsalPolicyService } from '@modules/core/services/msalPolicy.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  protected readonly baseApi: CustomerDetailsAPI;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private msalPolicyService: MsalPolicyService
  ) {
    const clientParams: CustomerDetailsAPIOptionalParams = {
      endpoint: `${env.nxApi.base}/${env.nxApi.fragment.customer}`,
      additionalPolicies: [
        { policy: new AddHeadersRequestPolicy(), position: 'perCall' },
      ],
      retryOptions: {
        maxRetries: 0,
      },
    };
    this.baseApi = new CustomerDetailsAPI(clientParams);
  }

  public createCustomer(
    customer: CreateCustomerRequest,
    token?: string
  ): Observable<CreateCustomerAccountResponse> {
    return from(
      this.baseApi.createCustomerAccount(token || '', { body: customer })
    );
  }

  public getCustomer(token?: string): Observable<GetCustomerAccountResponse> {
    try {
      return from(this.baseApi.getCustomerAccount(token || ''));
    } catch (ex) {
      return of({} as GetCustomerAccountResponse);
    }
  }

  public updateCustomer(
    customerId: string,
    customer: UpdateCustomerRequest,
    token?: string
  ): Observable<UpdateCustomerAccountResponse> {
    return from(
      this.baseApi.updateCustomerAccount(customerId, token || '', {
        body: customer,
      })
    );
  }

  public uploadProfileImage(
    customerId: string,
    contentType: ContentType,
    data: coreRestPipeline.RequestBodyType
  ): Observable<UploadCustomerAccountPhotoResponse> {
    return from(
      this.baseApi.uploadCustomerAccountPhoto(customerId, { contentType, data })
    );
  }

  public getProfileImage(
    customerId: string
  ): Observable<CustomerAccountProfileImageResponse> {
    return from(this.baseApi.customerAccountProfileImage(customerId));
  }

  public verifyMobileApp(
    emailAddress: string
  ): Observable<MobileAppVerifyResponse> {
    return from(this.baseApi.mobileAppVerify({ body: { emailAddress } }));
  }

  public verifySwiftCard(
    swiftCardNumber: string
  ): Observable<CheckSwiftCardResponse> {
    const encodedCardNumber = window.btoa(swiftCardNumber);
    return from(this.baseApi.checkSwiftCard(encodedCardNumber));
  }

  public getApplicantsForBillingCustomer(
    billingCustomerEmail: string
  ): Observable<GetCustomerApplicantsResponse> {
    return from(this.baseApi.getCustomerApplicants(billingCustomerEmail));
  }

  public getSwiftCardsForApplicant(
    billingCustomerEmail: string,
    applicantCustomerID: string,
    ticketID: number
  ): Observable<GetApplicantSwiftcardsResponse> {
    return from(
      this.baseApi.getApplicantSwiftcards({
        body: {
          billingCustomerEmail,
          applicantCustomerID,
          ticketID,
        },
      })
    );
  }
}
